import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Card, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import Company from "@icons/myicons/company.svg?react";
import User from "@icons/myicons/user.svg?react";
import Phone from "@icons/myicons/phone.svg?react";
import Bank from "@icons/myicons/bank.svg?react";
import MapMark from "@icons/myicons/map-mark.svg?react";
import { AccountHolder } from "@rentablo/api/apiv1";

const Wrapper = styled(Card)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(3, 4)};
  display: flex;
  flex-direction: column;
  flex-basis: 360px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-basis: 100%;
  }
`;

const PointerTypography = styled(Typography)`
  cursor: pointer;
`;

const Avatar = styled(Box)`
  border-radius: 50%;
  height: 48px;
  background-color: ${({ theme }) => theme.palette.background.neutral};
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.palette.primary.dark};
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const Body = styled.div`
  padding-top: ${({ theme }) => theme.spacing(6)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BodyItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export interface CashbackDashboardContactsProps {
  contact: AccountHolder;
}

export const CashbackDashboardContactsItem = (
  props: CashbackDashboardContactsProps
) => {
  const { contact } = props;
  const navigate = useNavigate();
  const { naturalPersons, referenceAccount } = contact;
  const trueUser = naturalPersons?.find((np) => np.isPrimaryAccountHolder);
  const { t } = useTranslation();

  return (
    <Wrapper>
      <Header>
        <Typography variant="body1" fontWeight="600">
          {t("domains.cashback.contact.owner")}
        </Typography>
        <PointerTypography
          variant="body4"
          color="primary"
          onClick={() => navigate(`/cashback/contact/edit/${contact.id}`)}
        >
          {t("domains.cashback.contact.edit")}
        </PointerTypography>
      </Header>
      <Body>
        <Avatar>
          {contact.type === "PRIVATE" ? (
            <User width="32px" height="32px" />
          ) : (
            <Company width="32px" height="32px" />
          )}
        </Avatar>
        <Typography variant="overline" color="textSecondary">
          {contact.type === "PRIVATE"
            ? t("domains.cashback.contact.person")
            : t("domains.cashback.contact.company")}
        </Typography>
        {trueUser && (
          <Typography variant="body4" mb={3}>
            {`${trueUser.firstName} ${trueUser.lastName}`}
          </Typography>
        )}
        {trueUser?.address && (
          <BodyItem mb={2}>
            <MapMark width="14px" height="18px" />
            <Typography variant="body3" color="textSecondary">
              {`${trueUser.address.city}, ${trueUser.address.region?.name}`}
            </Typography>
          </BodyItem>
        )}
        {referenceAccount && (
          <BodyItem mb={2}>
            <Bank width="17px" height="18px" />
            <Typography variant="body3" color="textSecondary">
              {`${referenceAccount?.bankName}`}
            </Typography>
          </BodyItem>
        )}
        <BodyItem>
          <Phone width="16px" height="16px" />
          <Typography variant="body3" color="textSecondary">
            {contact.userReference.phone}
          </Typography>
        </BodyItem>
      </Body>
    </Wrapper>
  );
};
